import React, { useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, List, Typography, Space, Tag, Badge, Switch, message, Divider, Input, Popconfirm, Row, Col, Select, Avatar } from 'antd';
import { UserOutlined, ClockCircleOutlined, CheckCircleOutlined, SendOutlined } from '@ant-design/icons';
import { MessageIcon } from '../../../utils/icons';
import { updateMessageProcessStatus, sendCustomerMessage } from '../actions';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const CustomerMessagesModal = ({ visible, onCancel, orderMessages, onMessageUpdate, orderId, amazonOrderId }) => {
    const dispatch = useDispatch();
    const { autoMessages } = useSelector(state => state.dataCommon);
    const [switchLoading, setSwitchLoading] = useState({});
    const [localMessages, setLocalMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);

    // Use autoMessages from Redux
    const availableTemplates = autoMessages || [];

    // Memoize dropdown options to prevent re-render issues
    const templateOptions = useMemo(() => {
        if (!availableTemplates || availableTemplates.length === 0) {
            return [{ value: 'no_templates', label: '⚠️ Chưa có mẫu tin nhắn', disabled: true }];
        }
        
        return availableTemplates.map(template => ({
            value: template.key,
            label: template.description || template.key
        }));
    }, [availableTemplates]);

    // Handle auto message template selection - Replace textarea content
    const handleTemplateSelect = useCallback((templateKey) => {
        const selectedTemplate = autoMessages?.find(template => template.key === templateKey);
        if (selectedTemplate && selectedTemplate.value) {
            const templateContent = selectedTemplate.value;
            
            // Check if template content exceeds limit
            if (templateContent.length > 2000) {
                message.error('Template vượt quá giới hạn 2000 ký tự');
                return;
            }
            
            // Replace entire content with template
            setNewMessage(templateContent);
            message.success(`Đã áp dụng template: ${selectedTemplate.description || selectedTemplate.key}`);
            
            // Focus textarea after a short delay
            setTimeout(() => {
                const textArea = document.querySelector('.customer-message-textarea');
                if (textArea) {
                    textArea.focus();
                    // Set cursor to end
                    textArea.setSelectionRange(templateContent.length, templateContent.length);
                }
            }, 100);
        }
    }, [autoMessages]);

    // Update local messages when orderMessages prop changes
    React.useEffect(() => {
        if (orderMessages && Array.isArray(orderMessages)) {
            setLocalMessages(orderMessages);
        }
    }, [orderMessages]);

    // Filter and sort messages using local state
    const sortedMessages = React.useMemo(() => {
        if (!localMessages || !Array.isArray(localMessages)) return [];

        return localMessages
            .filter(msg => msg.messageType === 'customer_message' || msg.messageType === 'user_message')
            .sort((a, b) => new Date(b.customerMessageDate || b.createdAt) - new Date(a.customerMessageDate || a.createdAt));
    }, [localMessages]);

    // Count unprocessed messages (only customer messages, not user messages)
    const unprocessedCount = React.useMemo(() => {
        return sortedMessages.filter(msg => msg.messageType === 'customer_message' && !msg.isProcessed).length;
    }, [sortedMessages]);

    const handleProcessStatusChange = async (messageId, checked) => {
        setSwitchLoading(prev => ({ ...prev, [messageId]: true }));

        try {
            const result = await dispatch(updateMessageProcessStatus(messageId, checked));

            if (result.success) {
                message.success(checked ? 'Message marked as processed' : 'Message marked as unprocessed');

                // Immediately update local state for instant UI feedback
                setLocalMessages(prevMessages =>
                    prevMessages.map(msg =>
                        msg.id === messageId
                            ? { ...msg, isProcessed: checked }
                            : msg
                    )
                );

                // Notify parent component to refresh data
                if (onMessageUpdate) {
                    onMessageUpdate();
                }
            } else {
                throw new Error(result.message || 'Failed to update message status');
            }
        } catch (error) {
            console.error('Error updating message status:', error);
            message.error(error.message || 'Failed to update message status');
        } finally {
            setSwitchLoading(prev => ({ ...prev, [messageId]: false }));
        }
    };

    const handleCancel = () => {
        // Reset local state when modal is closed
        setSwitchLoading({});
        setNewMessage('');
        setSendingMessage(false);
        if (onCancel) {
            onCancel();
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !orderId || !amazonOrderId) {
            message.error('Please enter a message');
            return;
        }

        setSendingMessage(true);
        try {
            const result = await dispatch(sendCustomerMessage({
                orderId,
                amazonOrderId,
                messageContent: newMessage.trim()
            }));

            if (result.success) {
                message.success('Message sent successfully');
                setNewMessage('');

                // Add the sent message to local state immediately for better UX
                const sentMessage = {
                    id: `temp-${Date.now()}`,
                    messageType: 'user_message',
                    messageContent: newMessage.trim(),
                    createdAt: new Date().toISOString(),
                    isProcessed: true,
                    processedAt: new Date().toISOString()
                };

                setLocalMessages(prevMessages => [sentMessage, ...prevMessages]);

                // Notify parent component to refresh data
                if (onMessageUpdate) {
                    onMessageUpdate();
                }
            } else {
                throw new Error(result.message || 'Failed to send message');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            message.error(error.message || 'Failed to send message');
        } finally {
            setSendingMessage(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown date';
        return dayjs(dateString).format('MMM DD, YYYY HH:mm');
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <MessageIcon style={{ color: '#1890ff', fontSize: '16px' }} />
                    <span>Customer Messages</span>
                    {unprocessedCount > 0 && (
                        <Badge
                            count={unprocessedCount}
                            style={{
                                backgroundColor: '#ff4d4f',
                                fontSize: '12px',
                                height: '18px',
                                lineHeight: '18px',
                                minWidth: '18px'
                            }}
                        />
                    )}
                    <span style={{ color: '#999', fontSize: '14px', marginLeft: '8px' }}>
                        ({sortedMessages.length} total)
                    </span>
                </div>
            }
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="close" onClick={handleCancel}>
                    Close
                </Button>
            ]}
            width={800}
            styles={{
                body: {
                    padding: '16px 0 0 0'
                }
            }}
        >

            <div style={{ 
                maxHeight: '50vh', 
                overflowY: 'auto', 
                padding: '8px',
                backgroundColor: '#fafafa',
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(140, 140, 140, 0.1) 1px, transparent 0)',
                backgroundSize: '15px 15px'
            }}>
                {sortedMessages.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        <MessageIcon style={{ fontSize: '48px', marginBottom: '16px', color: '#d9d9d9' }} />
                        <div>No customer messages found for this order</div>
                    </div>
                ) : (
                    <List
                        dataSource={sortedMessages}
                        split={false}
                        renderItem={(msg, index) => {
                            const isUserMessage = msg.messageType === 'user_message';
                            return (
                                <List.Item
                                    key={msg.id || index}
                                    style={{
                                        padding: '8px 16px',
                                        border: 'none',
                                        display: 'flex',
                                        justifyContent: isUserMessage ? 'flex-end' : 'flex-start'
                                    }}
                                >
                                    <div style={{
                                        maxWidth: '70%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: isUserMessage ? 'flex-end' : 'flex-start'
                                    }}>
                                        {/* Message Header */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            marginBottom: '4px',
                                            flexDirection: isUserMessage ? 'row-reverse' : 'row'
                                        }}>
                                            <Avatar
                                                size="small"
                                                icon={<UserOutlined />}
                                                style={{
                                                    backgroundColor: isUserMessage ? '#1890ff' : '#87d068'
                                                }}
                                            />
                                            <Text strong style={{ fontSize: '12px' }}>
                                                {isUserMessage ? 'You' : 'Customer'}
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                                {formatDate(msg.customerMessageDate || msg.createdAt)}
                                            </Text>
                                        </div>

                                        {/* Message Bubble */}
                                        <div style={{
                                            backgroundColor: isUserMessage ? '#1890ff' : '#f0f0f0',
                                            color: isUserMessage ? '#fff' : '#000',
                                            padding: '8px 12px',
                                            borderRadius: '12px',
                                            borderTopLeftRadius: isUserMessage ? '12px' : '4px',
                                            borderTopRightRadius: isUserMessage ? '4px' : '12px',
                                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                                            position: 'relative',
                                            wordWrap: 'break-word',
                                            wordBreak: 'break-word'
                                        }}>
                                            <Paragraph
                                                style={{
                                                    margin: 0,
                                                    fontSize: '14px',
                                                    whiteSpace: 'pre-wrap',
                                                    color: isUserMessage ? '#fff' : '#000'
                                                }}
                                            >
                                                {msg.messageContent}
                                            </Paragraph>
                                        </div>

                                        {/* Message Status and Actions */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            marginTop: '4px',
                                            flexDirection: isUserMessage ? 'row-reverse' : 'row'
                                        }}>
                                            <Tag
                                                icon={isUserMessage ? <CheckCircleOutlined /> :
                                                    (msg.isProcessed ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                                                        <ClockCircleOutlined style={{ color: '#faad14' }} />)
                                                }
                                                color={isUserMessage ? 'blue' : (msg.isProcessed ? 'success' : 'warning')}
                                                style={{
                                                    fontSize: '10px',
                                                    margin: 0,
                                                    borderRadius: '8px',
                                                    border: 'none'
                                                }}
                                                size="small"
                                            >
                                                {isUserMessage ? 'Sent' : (msg.isProcessed ? 'Processed' : 'Pending')}
                                            </Tag>

                                            {!isUserMessage && (
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    loading={switchLoading[msg.id]}
                                                    onClick={() => handleProcessStatusChange(msg.id, !msg.isProcessed)}
                                                    style={{
                                                        fontSize: '10px',
                                                        height: '24px',
                                                        padding: '0 6px',
                                                        color: msg.isProcessed ? '#52c41a' : '#faad14',
                                                        border: `1px solid ${msg.isProcessed ? '#52c41a' : '#faad14'}`
                                                    }}
                                                >
                                                    {msg.isProcessed ? 'Unprocess' : 'Process'}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                )}
            </div>
            {/* Chat Input Area */}
            <div style={{
                padding: '12px 16px',
                borderTop: '1px solid #e8e8e8',
                backgroundColor: '#fff',
                marginBottom: '10px'
            }}>
                <div style={{ marginBottom: '8px' }}>
                    <Text style={{ fontSize: '12px', color: '#666' }}>
                        Send message to customer
                    </Text>
                </div>
               
                <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-end'
                }}>
                    <div style={{ flex: 1 }}>
                        <TextArea
                            className="customer-message-textarea"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type your message..."
                            maxLength={2000}
                            rows={3}
                            style={{
                                borderRadius: '8px',
                                border: '1px solid #d9d9d9',
                                resize: 'none'
                            }}
                            onPressEnter={(e) => {
                                if (e.shiftKey) {
                                    return; // Allow new line with Shift+Enter
                                }
                                e.preventDefault();
                                if (newMessage.trim()) {
                                    handleSendMessage();
                                }
                            }}
                        />
                    </div>
                    <Popconfirm
                        title="Send message to customer?"
                        description="Are you sure you want to send this message?"
                        onConfirm={handleSendMessage}
                        okText="Send"
                        cancelText="Cancel"
                        disabled={!newMessage.trim() || sendingMessage}
                    >
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={sendingMessage}
                            disabled={!newMessage.trim()}
                            style={{
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        />
                    </Popconfirm>
                </div>
                <div style={{ marginTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* Auto Message Templates Dropdown */}
                    <Select
                        placeholder="Select template"
                        style={{ minWidth: 180}}
                        size="small"
                        allowClear
                        value={undefined} // Always reset after selection
                        onSelect={handleTemplateSelect}
                        onClear={() => message.info('Template selection cleared')}
                        popupMatchSelectWidth={400}
                        placement='topLeft'
                        options={templateOptions}
                        notFoundContent="Không tìm thấy mẫu tin nhắn"
                    />
                    
                    <Text type="secondary" style={{ fontSize: '10px' }}>
                        {newMessage.length}/2000 • Press Enter to send
                    </Text>
                </div>
            </div>
            {/* Summary Footer */}
            {sortedMessages.length > 0 && (
                <>
                    <Divider style={{ margin: '16px 0 12px 0' }} />
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0 16px',
                        fontSize: '13px'
                    }}>
                        <div style={{ display: 'flex', gap: '24px' }}>
                            <span>
                                <Text strong style={{ color: '#52c41a' }}>
                                    {sortedMessages.filter(m => m.isProcessed).length}
                                </Text>
                                <Text type="secondary"> processed</Text>
                            </span>
                            <span>
                                <Text strong style={{ color: '#faad14' }}>
                                    {unprocessedCount}
                                </Text>
                                <Text type="secondary"> pending</Text>
                            </span>
                            <span>
                                <Text strong style={{ color: '#595959' }}>
                                    {sortedMessages.length}
                                </Text>
                                <Text type="secondary"> total</Text>
                            </span>
                        </div>

                        {unprocessedCount > 0 && (
                            <Tag color="warning" style={{ margin: 0 }}>
                                <MessageIcon style={{ marginRight: '4px', fontSize: '12px' }} />
                                {unprocessedCount} need{unprocessedCount > 1 ? '' : 's'} attention
                            </Tag>
                        )}
                    </div>
                </>
            )}
        </Modal>
    );
};

export default CustomerMessagesModal;
